import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { obtenerEstadoTokenInstagram } from '../../scripts/obtenerEstadoTokenInstagram';
import { refrescarTokenInstagram } from '../../scripts/refrescarTokenInstagram';
import { actualizarInstagramCredenciales } from '../../scripts/actualizarInstagramCredenciales';

const Container = styled.div`
	padding: 2rem;
	max-width: 1200px;
	margin: 0 auto;
`;

const Title = styled.h2`
	font-size: 2rem;
	color: #333;
	margin-bottom: 2rem;
`;

const Card = styled.div`
	background: white;
	border-radius: 8px;
	padding: 2rem;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	margin-bottom: 2rem;
`;

const StatusIndicator = styled.div`
	display: inline-flex;
	align-items: center;
	padding: 0.5rem 1rem;
	border-radius: 20px;
	font-weight: 600;
	margin-bottom: 1rem;
	background-color: ${(props) => {
		if (props.status === 'good') return '#d4edda';
		if (props.status === 'warning') return '#fff3cd';
		return '#f8d7da';
	}};
	color: ${(props) => {
		if (props.status === 'good') return '#155724';
		if (props.status === 'warning') return '#856404';
		return '#721c24';
	}};
`;

const InfoRow = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 1rem 0;
	border-bottom: 1px solid #e0e0e0;

	&:last-child {
		border-bottom: none;
	}
`;

const Label = styled.span`
	font-weight: 600;
	color: #555;
`;

const Value = styled.span`
	color: #333;
	font-family: monospace;
`;

const ButtonGroup = styled.div`
	display: flex;
	gap: 1rem;
	margin-top: 2rem;
`;

const Button = styled.button`
	padding: 0.75rem 1.5rem;
	border-radius: 5px;
	border: none;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s ease;
	background-color: ${(props) => (props.secondary ? '#6c757d' : '#007bff')};
	color: white;

	&:hover {
		background-color: ${(props) => (props.secondary ? '#5a6268' : '#0056b3')};
		transform: translateY(-2px);
	}

	&:disabled {
		background-color: #ccc;
		cursor: not-allowed;
		transform: none;
	}
`;

const FormSection = styled.div`
	margin-top: 2rem;
`;

const FormGroup = styled.div`
	margin-bottom: 1.5rem;
`;

const Input = styled.input`
	width: 100%;
	padding: 0.75rem;
	border: 1px solid #ddd;
	border-radius: 5px;
	font-size: 1rem;
	font-family: monospace;

	&:focus {
		outline: none;
		border-color: #007bff;
	}
`;

const TextArea = styled.textarea`
	width: 100%;
	padding: 0.75rem;
	border: 1px solid #ddd;
	border-radius: 5px;
	font-size: 1rem;
	font-family: monospace;
	min-height: 100px;
	resize: vertical;

	&:focus {
		outline: none;
		border-color: #007bff;
	}
`;

const HelpText = styled.p`
	font-size: 0.875rem;
	color: #666;
	margin-top: 0.5rem;
	font-style: italic;
`;

const LoadingSpinner = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 3rem;
	font-size: 1.5rem;
	color: #666;
`;

const InstagramSettings = () => {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [updating, setUpdating] = useState(false);
	const [tokenStatus, setTokenStatus] = useState(null);
	const [showUpdateForm, setShowUpdateForm] = useState(false);
	const [credentials, setCredentials] = useState({
		user_id: '',
		access_token: '',
	});

	const loadTokenStatus = async () => {
		setLoading(true);
		try {
			const status = await obtenerEstadoTokenInstagram(dispatch);
			setTokenStatus(status);
		} catch (error) {
			console.error('Error al cargar estado del token:', error);
			toast.error('Error al cargar el estado del token de Instagram');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadTokenStatus();
	}, []);

	const handleRefreshToken = async () => {
		setRefreshing(true);
		try {
			await refrescarTokenInstagram(dispatch);
			await loadTokenStatus();
		} catch (error) {
			console.error('Error al refrescar token:', error);
		} finally {
			setRefreshing(false);
		}
	};

	const handleUpdateCredentials = async (e) => {
		e.preventDefault();
		setUpdating(true);
		try {
			await actualizarInstagramCredenciales(credentials, dispatch);
			await loadTokenStatus();
			setShowUpdateForm(false);
			setCredentials({ user_id: '', access_token: '' });
		} catch (error) {
			console.error('Error al actualizar credenciales:', error);
		} finally {
			setUpdating(false);
		}
	};

	const getStatusType = () => {
		if (!tokenStatus) return 'critical';
		if (tokenStatus.days_until_expiry > 7) return 'good';
		if (tokenStatus.days_until_expiry > 0) return 'warning';
		return 'critical';
	};

	const getStatusText = () => {
		if (!tokenStatus) return 'Estado desconocido';
		if (tokenStatus.days_until_expiry > 7)
			return `✓ Token válido (${tokenStatus.days_until_expiry} días restantes)`;
		if (tokenStatus.days_until_expiry > 0)
			return `⚠ Token expirando pronto (${tokenStatus.days_until_expiry} días restantes)`;
		return '✗ Token expirado - Acción requerida';
	};

	if (loading) {
		return (
			<Container>
				<LoadingSpinner>Cargando estado de Instagram...</LoadingSpinner>
			</Container>
		);
	}

	return (
		<Container>
			<Title>Configuración de Instagram</Title>

			<Card>
				<h3>Estado del Token</h3>
				<StatusIndicator status={getStatusType()}>{getStatusText()}</StatusIndicator>

				{tokenStatus && (
					<>
						<InfoRow>
							<Label>User ID:</Label>
							<Value>{tokenStatus.instagram_user_id || 'No configurado'}</Value>
						</InfoRow>
						<InfoRow>
							<Label>Token (Vista previa):</Label>
							<Value>{tokenStatus.token_preview || 'No configurado'}</Value>
						</InfoRow>
						<InfoRow>
							<Label>Fecha de expiración:</Label>
							<Value>
								{tokenStatus.token_expiry
									? new Date(tokenStatus.token_expiry).toLocaleString()
									: 'No disponible'}
							</Value>
						</InfoRow>
						<InfoRow>
							<Label>Días restantes:</Label>
							<Value>
								{tokenStatus.days_until_expiry >= 0 ? tokenStatus.days_until_expiry : 'Expirado'}
							</Value>
						</InfoRow>
					</>
				)}

				<ButtonGroup>
					<Button onClick={handleRefreshToken} disabled={refreshing}>
						{refreshing ? 'Refrescando...' : '🔄 Refrescar Token Ahora'}
					</Button>
					<Button secondary onClick={() => setShowUpdateForm(!showUpdateForm)}>
						{showUpdateForm ? 'Cancelar' : '⚙️ Actualizar Credenciales'}
					</Button>
				</ButtonGroup>
			</Card>

			{showUpdateForm && (
				<Card>
					<h3>Actualizar Credenciales de Instagram</h3>
					<HelpText>
						Para obtener un nuevo Long-Lived Access Token, visita el{' '}
						<a
							href="https://developers.facebook.com/tools/explorer/"
							target="_blank"
							rel="noopener noreferrer"
						>
							Graph API Explorer de Facebook
						</a>
						.
					</HelpText>

					<form onSubmit={handleUpdateCredentials}>
						<FormSection>
							<FormGroup>
								<Label>User ID de Instagram:</Label>
								<Input
									type="text"
									value={credentials.user_id}
									onChange={(e) => setCredentials({ ...credentials, user_id: e.target.value })}
									placeholder="Ejemplo: 8816356138401496"
									required
								/>
							</FormGroup>

							<FormGroup>
								<Label>Access Token:</Label>
								<TextArea
									value={credentials.access_token}
									onChange={(e) => setCredentials({ ...credentials, access_token: e.target.value })}
									placeholder="Pega aquí tu Long-Lived Access Token..."
									required
								/>
								<HelpText>
									El token será validado antes de guardarse. Asegúrate de usar un Long-Lived Access
									Token que dura 60 días.
								</HelpText>
							</FormGroup>
						</FormSection>

						<ButtonGroup>
							<Button type="submit" disabled={updating}>
								{updating ? 'Validando y guardando...' : '💾 Guardar Credenciales'}
							</Button>
							<Button type="button" secondary onClick={() => setShowUpdateForm(false)}>
								Cancelar
							</Button>
						</ButtonGroup>
					</form>
				</Card>
			)}

			<Card>
				<h3>ℹ️ Información sobre Tokens de Instagram</h3>
				<InfoRow>
					<Label>Tipo de Token:</Label>
					<Value>Long-Lived Access Token</Value>
				</InfoRow>
				<InfoRow>
					<Label>Duración:</Label>
					<Value>60 días</Value>
				</InfoRow>
				<InfoRow>
					<Label>Refresco Automático:</Label>
					<Value>Cada día a las 3:00 AM (si expira en menos de 7 días)</Value>
				</InfoRow>
				<InfoRow>
					<Label>Refresco Manual:</Label>
					<Value>Disponible en cualquier momento desde este panel</Value>
				</InfoRow>
				<HelpText style={{ marginTop: '1rem' }}>
					El sistema verificará automáticamente el estado del token diariamente y lo refrescará si está
					próximo a expirar. También puedes refrescarlo manualmente en cualquier momento usando el botón de
					arriba.
				</HelpText>
			</Card>
		</Container>
	);
};

export default InstagramSettings;
